
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ContentBlockRenderer from './ContentBlockRenderer';
import AuthorInfo from '../common/AuthorInfo';
import ArticleRating from '../common/ArticleRating';
import CommentSection from '../common/CommentSection';
import LikeButton from '../common/LikeButton';
import { ContentBlockType, ContentPage } from '../../types/content';
import { contentService } from '../../services/contentService';
import { Loader2, AlertCircle } from 'lucide-react';

interface DynamicContentPageProps {
  slug?: string;
  pageOverride?: ContentPage;
  showAuthorInfo?: boolean;
  showRating?: boolean;
  showComments?: boolean;
  className?: string;
  noWrapper?: boolean;
  markCompleteButton?: React.ReactNode;
}

const DynamicContentPage: React.FC<DynamicContentPageProps> = ({
  slug: propSlug,
  pageOverride,
  showAuthorInfo = true,
  showRating = true,
  showComments = true,
  className = '',
  noWrapper = false,
  markCompleteButton,
}) => {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const slug = propSlug || routeSlug;

  const [page, setPage] = useState<ContentPage | null>(pageOverride || null);
  const [loading, setLoading] = useState(!pageOverride);
  const [error, setError] = useState<string | null>(null);
  const viewCountIncremented = useRef<Set<string>>(new Set()); // Track which slugs we've incremented in this component instance

  useEffect(() => {
    if (pageOverride) {
      setPage(pageOverride);
      setError(null);
      setLoading(false);
      return;
    }

    if (!slug) {
      setError('No slug provided');
      setLoading(false);
      return;
    }

    const fetchPage = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if we've already incremented view count for this slug
        // This prevents double-counting when useEffect runs multiple times (e.g., React StrictMode)
        const hasIncremented = viewCountIncremented.current.has(slug);

        // Fetch page - backend will increment view count on first call
        const data = await contentService.getBySlug(slug);
        setPage(data);

        // Mark this slug as incremented to prevent double-counting
        if (!hasIncremented) {
          viewCountIncremented.current.add(slug);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load content page');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();

    // Cleanup: remove slug from set when component unmounts or slug changes
    return () => {
      // Don't clear on unmount, only when slug changes
      // This allows tracking across re-renders but resets when navigating to a different page
    };
  }, [slug, pageOverride]);

  // Scroll to top when slug changes (new page loaded)
  useEffect(() => {
    if (slug && !loading) {
      // Immediate scroll first
      const contentArea = document.querySelector('[data-content-area]') as HTMLElement;
      if (contentArea) {
        contentArea.scrollTo({ top: 0, behavior: 'auto' });
      }
      window.scrollTo({ top: 0, behavior: 'auto' });

      // Small delay to ensure content is rendered, then smooth scroll
      setTimeout(() => {
        const contentArea = document.querySelector('[data-content-area]') as HTMLElement;
        if (contentArea) {
          contentArea.scrollTo({ top: 0, behavior: 'smooth' });
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 150);
    }
  }, [slug, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Content Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {error || 'The requested content page could not be found.'}
          </p>
        </div>
      </div>
    );
  }

  // Sort blocks by order
  const sortedBlocks = [...page.blocks].sort((a, b) => a.order - b.order);

  // Keep a subheading and its supporting explanation visually together. The
  // content API stores each piece as a separate block, so without this grouping
  // comparisons such as CP/AP choices read like unrelated fragments.
  const renderedBlocks: React.ReactNode[] = [];
  for (let index = 0; index < sortedBlocks.length;) {
    const block = sortedBlocks[index];
    if (block.type === ContentBlockType.HEADING && block.level === 5) {
      const subsection = [block];
      let cursor = index + 1;
      while (cursor < sortedBlocks.length) {
        const next = sortedBlocks[cursor];
        if (next.type === ContentBlockType.HEADING && (next.level || 2) <= 5) break;
        subsection.push(next);
        cursor += 1;
      }
      renderedBlocks.push(
        <div className="article-subsection" key={`subsection-${block.order}`}>
          {subsection.map((item, subsectionIndex) => (
            <ContentBlockRenderer key={`sub-block-${subsectionIndex}-${item.order}`} block={item} />
          ))}
        </div>
      );
      index = cursor;
      continue;
    }
    renderedBlocks.push(<ContentBlockRenderer key={`block-${index}-${block.order}`} block={block} />);
    index += 1;
  }

  // HLD lessons are shipped as static article content, so their likes use the
  // article-like API rather than the database content-page endpoint.
  const likeSource = page.tags?.includes('hld') || page.subcategory === 'hld' ? 'article' : 'content';

  const content = (
    <>
      {/* Page Header */}
      <div className="mb-12 pb-8 border-b-2 border-gray-300 dark:border-gray-700">
        <h1 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100 mb-6 tracking-tight">
          {page.title}
        </h1>

        {page.description && (
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
            {page.description}
          </p>
        )}

        <div className="flex items-center justify-between flex-wrap gap-4">
          {showAuthorInfo && page.author && (
            <AuthorInfo
              name={page.author.name}
              fullName={page.author.fullName}
              avatar={page.author.avatar}
              lastUpdated={page.lastUpdated}
              readTime={page.readTime}
              size="md"
            />
          )}

          {/* Like Button */}
          <LikeButton
            contentPageId={page.id}
            initialLikeCount={page.likeCount || 0}
            source={likeSource}
          />
        </div>
      </div>

      {/* Content Blocks */}
      <div className="max-w-none prose prose-lg dark:prose-invert prose-headings:font-bold prose-p:leading-relaxed prose-code:bg-gray-100 prose-code:dark:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-transparent [&>h4:first-child]:border-t-0 [&>h4:first-child]:mt-0 [&>h4:first-child]:pt-0 [&>h5:first-child]:border-t-0 [&>h5:first-child]:mt-0 [&>h5:first-child]:pt-0">
        {renderedBlocks}
      </div>

      {/* Article Rating + Mark Complete */}
      {showRating && (
        <div className="mt-12">
          <ArticleRating
            articleId={page.id}
            initialAverageRating={page.averageRating}
            initialRatingCount={page.ratingCount}
          />
          {markCompleteButton && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-6">
                {markCompleteButton}
              </div>
            </>
          )}
        </div>
      )}

      {/* Comment Section */}
      {showComments && (
        <div className="mt-12">
          <CommentSection articleId={page.id} />
        </div>
      )}
    </>
  );

  // If noWrapper is true, return just the content (for PracticeContentLayout)
  if (noWrapper) {
    return <div className={className}>{content}</div>;
  }

  // Otherwise, return with full wrapper (for standalone pages)
  return (
    <div className={`min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900 pt-16 pb-8 ${className}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
          {content}
        </div>
      </div>
    </div>
  );
};

export default DynamicContentPage;
